// src/components/LowRound/RoundCard.js
import React from "react";
import { Card, CardContent, Typography, Divider } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { formatInTimeZone } from "date-fns-tz";

// Helper: Convert a Firestore timestamp or ISO string to a Date.
const toDate = (value) => {
  if (!value) return null;
  if (typeof value === "object" && value.seconds) return new Date(value.seconds * 1000);
  return new Date(value);
};

// Format a Date in Eastern Time.
export const formatDateInEastern = (date) =>
  formatInTimeZone(date, "America/New_York", "EEE, MMM d, yyyy");

// Helper: Given a placement index (0-based), return a placement label and color.
export const getPlacementLabelAndColor = (index) => {
  const rank = index + 1;
  if (rank === 1) return { label: "1st Place", color: "#FFD700" };
  if (rank === 2) return { label: "2nd Place", color: "#C0C0C0" };
  if (rank === 3) return { label: "3rd Place", color: "#cd7f32" };
  return { label: `${rank}th Place`, color: "inherit" };
};

// Helper: Return a Typography variant based on placement.
export const getPlacementVariant = (index) => {
  const rank = index + 1;
  if (rank === 1) return "h4";
  if (rank === 2) return "h5";
  if (rank === 3) return "h6";
  return "subtitle1";
};

// Compute totals for a round's scores.
export const computeTotals = (scoreArray) => {
  let totalScore = 0;
  let totalPar = 0;
  if (Array.isArray(scoreArray)) {
    scoreArray.forEach((s) => {
      totalScore += s.score || 0;
      totalPar += s.par || 0;
    });
  }
  return { totalScore, totalPar };
};

const RoundCard = ({ round, placementIndex, userName, onSelect }) => {
  // Destructure both the label and its color.
  const { label, color } = getPlacementLabelAndColor(placementIndex);
  const placementVariant = getPlacementVariant(placementIndex);
  const dateObj = toDate(round.roundDate);
  const header = `${round.clubName || "Unknown Club"} - ${
    dateObj ? formatDateInEastern(dateObj) : "No Date"
  }`;
  const { totalScore, totalPar } = computeTotals(round.scores);
  const scoreDiff = totalScore - totalPar;

  return (
    <Card variant="outlined" sx={{ cursor: "pointer" }} onClick={onSelect}>
      <CardContent>
        <Typography
          variant={placementVariant}
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            color: color
          }}
        >
          <EmojiEventsIcon sx={{ mr: 0.5, fontSize: "inherit", color: color }} />
          {label}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {header}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Player: {userName}
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="body2">
          Total: {totalScore} ({scoreDiff >= 0 ? `+${scoreDiff}` : scoreDiff})
        </Typography>
        {round.differential !== undefined && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Differential: {round.differential >= 0 ? `+${round.differential.toFixed(1)}` : round.differential.toFixed(1)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default RoundCard;